<!-- =========================================================================================
    File Name: DashboardEcommerce.vue
    Description: Dashboard - Ecommerce
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template> 
    <div>
        <div class="vx-row">
            <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
                <statistics-card-line
                  v-if="customersSynced.analyticsData"
                  icon="UsersIcon"
                  :statistic="customersSynced.analyticsData.subscribers | k_formatter"
                  statisticTitle="Customers Synced"
                  :chartData="customersSynced.series"
                  v-bind:key="customersSynced.analyticsData.subscribers"
                  type="area" />
            </div>

            <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
                <statistics-card-line
                  v-if="revenueGenerated.analyticsData"
                  icon="DollarSignIcon"
                  :statistic="revenueGenerated.analyticsData.revenue | k_formatter"
                  statisticTitle="Revenue Generated"
                  :chartData="revenueGenerated.series"
                  v-bind:key="revenueGenerated.analyticsData.revenue"
                  color="success"
                  type="area" />
            </div>

            <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
                <statistics-card-line
                  v-if="customersErrors.analyticsData"
                  icon="ShoppingCartIcon"
                  :statistic="customersErrors.analyticsData.sales"
                  statisticTitle="All Sync Errors"
                  :chartData="customersErrors.series"
                  v-bind:key="customersErrors.analyticsData.sales"
                  color="danger"
                  type="area" />
            </div>
            <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
                <statistics-card-line
                  v-if="ordersRecevied.analyticsData"
                  icon="ShoppingBagIcon"
                  :statistic="ordersRecevied.analyticsData.orders | k_formatter"
                  statisticTitle="Orders Synced"
                  :chartData="ordersRecevied.series"
                  v-bind:key="ordersRecevied.analyticsData.orders"
                  color="warning"
                  type="area" />
            </div>
        </div>

        <div class="vx-row flex">

            <!-- LINE CHART -->
            <div class="vx-col w-full md:w-2/3 mb-base">
                <vx-card title="Revenue">
                    <template slot="actions">
                        <feather-icon icon="SettingsIcon" svgClasses="w-6 h-6 text-grey"></feather-icon>
                    </template>
                    <div slot="no-body" class="p-6 pb-0">
                        <div class="flex" v-if="revenueComparisonLine.analyticsData">
                            <div class="mr-6">
                                <p class="mb-1 font-semibold">This Month</p>
                                <p class="text-3xl text-success"><sup class="text-base mr-1">$</sup>{{ revenueComparisonLine.analyticsData.thisMonth.toLocaleString() }}</p>
                            </div>
                            <div>
                                <p class="mb-1 font-semibold">Last Month</p>
                                <p class="text-3xl"><sup class="text-base mr-1">$</sup>{{ revenueComparisonLine.analyticsData.lastMonth.toLocaleString() }}</p>
                            </div>
                        </div>
                        <vue-apex-charts
                          type="line"
                          height="266"
                          :options="revenueComparisonLineChartOptions.chartOptions"
                          :series="revenueComparisonLine.series" />
                    </div>
                </vx-card>
            </div>

            <!-- RADIAL CHART -->
            <div class="vx-col w-full md:w-1/3 mb-base">
                <vx-card title="Orders Completed" style="height: 100%;">
                    <template slot="actions">
                        <feather-icon icon="HelpCircleIcon" svgClasses="w-6 h-6 text-grey"></feather-icon>
                    </template>

                    <!-- CHART -->
                    <template slot="no-body">
                        <div class="mt-10">
                            <vue-apex-charts type="radialBar" height="240" :options="goalOverviewRadialBar.chartOptions" :series="[ this.$store.state.AppActiveUser.goalOverview.series ]" />
                        </div>
                    </template>

                    <!-- DATA -->
                    <div class="flex justify-between text-center mt-6" slot="no-body-bottom">
                        <div class="w-1/2 border border-solid d-theme-border-grey-light border-r-0 border-b-0 border-l-0">
                            <p class="mt-4">Completed</p>
                            <p class="mb-4 text-3xl font-semibold">{{ orderCompleted | orderCount('status', 'completed').length }}</p>
                        </div>
                        <div class="w-1/2 border border-solid d-theme-border-grey-light border-r-0 border-b-0">
                            <p class="mt-4">In Progress</p>
                            <p class="mb-4 text-3xl font-semibold">{{ orderCompleted | orderCount('status', 'processing','pending').length }}</p>
                        </div> 
                        <div class="w-1/2 border border-solid d-theme-border-grey-light border-r-0 border-b-0">
                            <p class="mt-4">Failed</p>
                            <p class="mb-4 text-3xl font-semibold">{{ orderCompleted | orderCount('status', 'failed','cancelled', 'on-hold','refunded').length }}</p>
                        </div>                        
                    </div>
                </vx-card>
            </div>
        </div>

        <div class="vx-row">
            <!-- CARD 9: DISPATCHED ORDERS -->
            <div class="vx-col w-full">
                <vx-card title="Recent Orders">
                <div slot="no-body" class="mt-4">
                    <vs-table :data="this.$store.state.AppActiveUser.dashboardOrders" class="table-dark-inverted">
                    <template slot="thead">
                        <vs-th>ORDER NO.</vs-th>
                        <vs-th>STATUS</vs-th>
                        <vs-th>AMOUNT</vs-th>
                        <vs-th>CUSTOMER NAME</vs-th>
                        <vs-th>DEAL STAGE</vs-th>
                        <vs-th>ITEMS PURCHASED</vs-th>
                        <vs-th>ORDER DATE</vs-th>
                    </template>

                    <template slot-scope="{data}">
                        <vs-tr v-bind:key="indextr.id" v-for="(tr, indextr) in data">
                            <vs-td :data="data[indextr].id">
                                <span>#{{data[indextr].id}}</span>
                            </vs-td>
                            <vs-td :data="data[indextr].status">
                                <span class="flex items-center px-2 py-1 rounded"><div class="h-3 w-3 rounded-full mr-2" :class="'bg-' + getOrderStatusColor(data[indextr].status) "></div>{{data[indextr].status}}</span>
                            </vs-td>
                            <vs-td :data="data[indextr].id">
                                <span>{{data[indextr].price}}</span>
                            </vs-td>
                            <vs-td :data="data[indextr].id">
                                <span>{{data[indextr].customer}}</span>
                            </vs-td>
                            <vs-td :data="data[indextr].id">
                                <span>{{dealStatus(data[indextr].status)}}</span>
                                <vs-progress :percent="dealPercent(data[indextr].status)" :color="getOrderStatusColor(data[indextr].status)"></vs-progress>
                            </vs-td>
                            <vs-td :data="data[indextr].id">
                                <span>{{data[indextr].products}}</span>
                            </vs-td>
                            <vs-td :data="data[indextr].id">
                                <span>{{getFormatedDate(data[indextr].date)}}</span>
                            </vs-td>
                        </vs-tr>
                    </template>
                    </vs-table>
                </div>

                </vx-card>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import VueApexCharts from 'vue-apexcharts'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import ChangeTimeDurationDropdown from '@/components/ChangeTimeDurationDropdown.vue'

export default{
  components: {
    VueApexCharts,
    StatisticsCardLine,
    VuePerfectScrollbar,
    ChangeTimeDurationDropdown
  },
  data () {
    return {
		customersSynced: {
			series: [
			{
				name: 'Customers',
				data: [0, this.$store.state.AppActiveUser.cards.customerHistory.length ? this.$store.state.AppActiveUser.cards.customerHistory.length : 0.1]
			}
			],
			analyticsData: {
			subscribers: this.$store.state.AppActiveUser.allCustomers
			}
		},
		revenueGenerated: {
			series: [
			{
				name: 'Revenue',
				data: this.$store.state.AppActiveUser.revenueSeries
			}
			],
			analyticsData: {
                revenue: this.$store.state.AppActiveUser.revenueGenerated
			}
		},
		customersErrors: {
			series: [
			{
				name: 'Errors',
				data: [0, parseInt(this.$store.state.AppActiveUser.cards.customerErrors.length + this.$store.state.AppActiveUser.cards.orderErrors.length + this.$store.state.AppActiveUser.cards.productErrors.length) ? parseInt(this.$store.state.AppActiveUser.cards.customerErrors.length + this.$store.state.AppActiveUser.cards.orderErrors.length + this.$store.state.AppActiveUser.cards.productErrors.length) : 0.1 ]
			}
			],
			analyticsData: {
			sales: this.$store.state.AppActiveUser.allErrors
			}
		},
		ordersRecevied: {
			series: [
			{
				name: 'Orders',
				data: [0, this.$store.state.AppActiveUser.cards.orderHistory.length ? this.$store.state.AppActiveUser.cards.orderHistory.length : 0.1]
			}
			],
			analyticsData: {
			orders: this.$store.state.AppActiveUser.allOrders
			}
        },
        goalOverviewRadialBar: {
            // series: [83],
            chartOptions: {
            plotOptions: {
                radialBar: {
                size: 110,
                startAngle: -150,
                endAngle: 150,
                hollow: {
                    size: '77%'
                },
                track: {
                    background: '#bfc5cc',
                    strokeWidth: '50%'
                },
                dataLabels: {
                    name: {
                    show: false
                    },
                    value: {
                    offsetY: 18,
                    color: '#99a2ac',
                    fontSize: '4rem'
                    }
                }
                }
            },
            colors: ['#00db89'],
            fill: {
                type: 'gradient',
                gradient: {
                shade: 'dark',
                type: 'horizontal',
                shadeIntensity: 0.5,
                gradientToColors: ['#00b5b5'],
                inverseColors: true,
                opacityFrom: 1,
                opacityTo: 1,
                stops: [0, 100]
                }
            },
            stroke: {
                lineCap: 'round'
            },
            chart: {
                sparkline: {
                enabled: true
                },
                dropShadow: {
                enabled: true,
                blur: 3,
                left: 1,
                top: 1,
                opacity: 0.1
                }
            }
            }
        },
        revenueComparisonLineChartOptions: {
            chartOptions: {
                chart: {
                    toolbar: { show: false },
                    dropShadow: {
                    enabled: true,
                    top: 5,
                    left: 0,
                    blur: 4,
                    opacity: 0.10
                    }
                },
                stroke: {
                    curve: 'smooth',
                    dashArray: [0, 8],
                    width: [4, 2]
                },
                grid: {
                    borderColor: '#e7e7e7'
                },
                legend: {
                    show: false
                },
                colors: ['#169E78', '#b8c2cc'],
                fill: {
                    type: 'gradient',
                    gradient: {
                    shade: 'dark',
                    inverseColors: false,
                    gradientToColors: ['#169E78', '#b8c2cc'],
                    shadeIntensity: 1,
                    type: 'horizontal',
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100, 100, 100]
                    }
                },
                markers: {
                    size: 0,
                    hover: {
                    size: 5
                    }
                },
                xaxis: {
                    labels: {
                    style: {
                        cssClass: 'text-grey fill-current'
                    }
                    },
                    axisTicks: {
                    show: false
                    },
                    categories: ['01', '', '', '', '05', '', '', '', '09', '', '', '', '13', '', '', '', '17', '', '', '', '21', '', '', '', '26', '', '', '', '31'],
                    axisBorder: {
                    show: false
                    }
                },
                yaxis: {
                    tickAmount: 5,
                    labels: {
                    style: {
                        cssClass: 'text-grey fill-current'
                    },
                    formatter (val) {
                        return val > 999 ? `${(val / 1000).toFixed(1)}k` : val
                    }
                    }
                },
                tooltip: {
                    x: { show: false }
                }
            }
        },
        revenueComparisonLine: {
            analyticsData: {
                thisMonth: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).thisMonth,
                lastMonth: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).lastMonth
            },
            series: [
            {
                name: 'This Month',
                data: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).thisMonthData
            },
            {
                name: 'Last Month',
                data: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).lastMonthData
            }
            ]
        },
        orderCompleted: this.$store.state.AppActiveUser.cards.orderHistory,
    }
  },
  created: function () {
    //console.log( Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders) )
  },
  methods: {
    dealPercent(status){
        switch (status) {
            case "pending":
            return 50;
            
            case "failed":
            return 60;
            
            case "processing":
            return 80;
            
            case "cancelled":
            return 80;
            
            case "completed":
            return 100;
            
            case "on-hold":
            return 60;
            
            case "refunded":
            return 100;
            
            default:
            return 30;
        }
    },
    dealStatus(status){
        switch (status) {
            case "pending":
            return "checkout pending";
            
            case "failed":
            return "checkout abandoned";
            
            case "processing":
            return "checkout completed";
            
            case "cancelled":
            return "cancelled";
            
            case "completed":
            return "shipped";
            
            case "on-hold":
            return "checkout pending";
            
            case "refunded":
            return "processed";
            
            default:
            return "checkout abandoned";
        }
    },
    getOrderStatusColor (status) {
      if (status === 'cancelled' || status === 'pending' || status === 'refunded')   return 'dark'
      if (status === 'completed') return 'primary'
      if (status === 'processing')   return 'success'
      if (status === 'on_hold') return 'warning'
      if (status === 'failed')  return 'danger'
      return 'warning'
    },
    getFormatedDate(thedate) {  
      var dt = new Date( Number(thedate) );
      var dd = dt.getDate();
      var mm = dt.getMonth() + 1; //January is 0!
      var yyyy = dt.getFullYear();
      if (dd < 10) { dd = '0' + dd; } 
      if (mm < 10) { mm = '0' + mm; } 
      
      return mm + '-' + dd + '-' + yyyy + " " + dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
    },
  },
  watch: {
    '$store.state.AppActiveUser.allOrders': function() {
        this.customersSynced = {
			series: [
			{
				name: 'Customers',
				data: [0, this.$store.state.AppActiveUser.cards.customerHistory.length ? this.$store.state.AppActiveUser.cards.customerHistory.length : 0.1]
			}
			],
			analyticsData: {
			subscribers: this.$store.state.AppActiveUser.allCustomers
			}
        },
        this.revenueGenerated = {
			series: [
			{
				name: 'Revenue',
				data: this.$store.state.AppActiveUser.revenueSeries
			}
			],
			analyticsData: {
                revenue: this.$store.state.AppActiveUser.revenueGenerated
			}
		},
		this.customersErrors = {
			series: [
			{
				name: 'Errors',
				data: [0, parseInt(this.$store.state.AppActiveUser.cards.customerErrors.length + this.$store.state.AppActiveUser.cards.orderErrors.length + this.$store.state.AppActiveUser.cards.productErrors.length) ? parseInt(this.$store.state.AppActiveUser.cards.customerErrors.length + this.$store.state.AppActiveUser.cards.orderErrors.length + this.$store.state.AppActiveUser.cards.productErrors.length) : 0.1 ]
			}
			],
			analyticsData: {
			sales: this.$store.state.AppActiveUser.allErrors
			}
		},
		this.ordersRecevied = {
			series: [
			{
				name: 'Orders',
				data: [0, this.$store.state.AppActiveUser.cards.orderHistory.length ? this.$store.state.AppActiveUser.cards.orderHistory.length : 0.1]
			}
			],
			analyticsData: {
			orders: this.$store.state.AppActiveUser.allOrders
			}
        },
        this.revenueComparisonLine = {
            analyticsData: {
                thisMonth: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).thisMonth,
                lastMonth: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).lastMonth
            },
            series: [
            {
                name: 'This Month',
                data: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).thisMonthData
            },
            {
                name: 'Last Month',
                data: Vue.filter('ordersByDay')(this.$store.state.AppActiveUser.groupedOrders).lastMonthData
            }
            ]
        },
        this.orderCompleted = this.$store.state.AppActiveUser.cards.orderHistory        
    }
  }
}
</script>

<style lang="scss">
.chat-card-log {
    height: 400px;

    .chat-sent-msg {
        background-color: #f2f4f7 !important;
    }
}
</style>
